import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Typography,
  Skeleton,
  Chip,
  Button,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { useWatchlistWithPrices } from "../../hooks/useWatchlist";
import { useNavigate } from "react-router-dom";

interface WatchlistItem {
  id: string;
  symbol: string;
  currentPrice?: number;
  priceChange?: number;
  priceChangePercent?: number;
}

interface WatchlistTableProps {
  watchlistId: string;
  onRemoveItem: (itemId: string) => void;
}

export const WatchlistTable: React.FC<WatchlistTableProps> = ({
  watchlistId,
  onRemoveItem,
}) => {
  const { watchlist, loading } = useWatchlistWithPrices(watchlistId);
  const navigate = useNavigate();

  const handleCreateTrade = (symbol: string) => {
    navigate("/trades", { state: { symbol } });
  };

  const handleViewTrades = (symbol: string) => {
    navigate("/trades", { state: { filterSymbol: symbol } });
  };

  if (loading) {
    return (
      <Box>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1 }} />
        ))}
      </Box>
    );
  }

  if (!watchlist?.items?.length) {
    return (
      <Box textAlign="center" py={4}>
        <Typography color="text.secondary">
          No symbols in this watchlist. Add some to get started!
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Symbol</TableCell>
            <TableCell align="right">Price</TableCell>
            <TableCell align="right">Change</TableCell>
            <TableCell align="right">Change %</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {watchlist.items.map((item: WatchlistItem) => (
            <TableRow key={item.id}>
              <TableCell>
                <Typography variant="body1" fontWeight="bold">
                  {item.symbol}
                </Typography>
              </TableCell>
              <TableCell align="right">
                {item.currentPrice ? `$${item.currentPrice.toFixed(2)}` : "N/A"}
              </TableCell>
              <TableCell align="right">
                {item.priceChange !== null && item.priceChange !== undefined ? (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-end"
                  >
                    {item.priceChange >= 0 ? (
                      <TrendingUpIcon fontSize="small" color="success" />
                    ) : (
                      <TrendingDownIcon fontSize="small" color="error" />
                    )}
                    <Typography
                      variant="body2"
                      color={
                        item.priceChange >= 0 ? "success.main" : "error.main"
                      }
                      ml={0.5}
                    >
                      {item.priceChange >= 0 ? "+" : ""}
                      {item.priceChange.toFixed(2)}
                    </Typography>
                  </Box>
                ) : (
                  "N/A"
                )}
              </TableCell>
              <TableCell align="right">
                {item.priceChangePercent !== null &&
                item.priceChangePercent !== undefined ? (
                  <Chip
                    label={`${item.priceChangePercent >= 0 ? "+" : ""}${item.priceChangePercent.toFixed(2)}%`}
                    color={item.priceChangePercent >= 0 ? "success" : "error"}
                    size="small"
                  />
                ) : (
                  "N/A"
                )}
              </TableCell>
              <TableCell align="center">
                <Box display="flex" gap={1} justifyContent="center">
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleCreateTrade(item.symbol)}
                  >
                    Trade
                  </Button>
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleViewTrades(item.symbol)}
                  >
                    View
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() => onRemoveItem(item.id)}
                    color="error"
                    aria-label="delete"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
